/**
 * SessionNotes.jsx — Session plan panel + sitewide banner.
 *
 * Storage: Cloudflare KV via worker (not localStorage)
 *   GET/PUT/DELETE /session/note  →  active note
 *   GET/PUT        /session/log   →  historical log (journal)
 *
 * Expiry: note expires at the next market-day 4 PM ET.
 *   - Weekends: expiry is pushed to Monday 4 PM ET.
 *   - Holidays are not modelled (would need a calendar API).
 *   - If 4 PM ET has already passed today, expiry is next trading day.
 *
 * Placeholder: loaded from /settings → sessionPlanPlaceholder.
 *   Set in Settings page → propagated here via prop.
 */

import { useState, useEffect, useCallback } from 'react';
import { sessionNoteGet, sessionNotePut, sessionNoteDelete, sessionLogGet, sessionLogPut } from '../../lib/api';

// ── Next market-close timestamp ───────────────────────────────────────────────
// Returns Unix ms of the next weekday 4:00 PM ET that hasn't passed yet.
// Uses Intl.DateTimeFormat so DST transitions are handled by the JS runtime.
function nextMarketClose() {
  const now = new Date();

  // Get the ET UTC-offset in minutes at an arbitrary UTC time on a given date string (YYYY-MM-DD).
  // We probe 20:00 UTC which is always between 14:00–16:00 ET regardless of DST.
  function etOffsetMinutesOn(dateStr) {
    const probe = new Date(`${dateStr}T20:00:00Z`);
    const etParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(probe);
    const p = Object.fromEntries(etParts.map(x => [x.type, x.value]));
    const etUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return (probe.getTime() - etUTC) / 60000; // positive = ET is behind UTC
  }

  // Walk forward at most 7 days to find the next weekday 4 PM ET in the future
  for (let daysAhead = 0; daysAhead <= 7; daysAhead++) {
    const candidate = new Date(now);
    candidate.setUTCDate(candidate.getUTCDate() + daysAhead);
    const dateStr = candidate.toISOString().slice(0, 10); // YYYY-MM-DD UTC

    // Get weekday in ET for this UTC date
    const dayFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', weekday: 'short',
    });
    const weekday = dayFmt.format(candidate);
    if (weekday === 'Sat' || weekday === 'Sun') continue;

    // Build 4 PM ET as UTC: 16:00 ET = 16:00 + offsetMinutes/60 UTC
    const offsetMins = etOffsetMinutesOn(dateStr);
    const close4pmUTC = new Date(`${dateStr}T16:00:00Z`);
    close4pmUTC.setUTCMinutes(close4pmUTC.getUTCMinutes() + offsetMins);

    if (close4pmUTC > now) return close4pmUTC.getTime();
  }

  // Fallback (should never trigger)
  return now.getTime() + 7 * 24 * 3600 * 1000;
}

function isExpired(note) {
  if (!note) return true;
  return Date.now() > note.expiresAt;
}

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── SessionNotesBanner ────────────────────────────────────────────────────────
export function SessionNotesBanner({ note, onEdit, onDismiss }) {
  if (!note || isExpired(note)) return null;

  const preview = note.text.length > 160 ? note.text.slice(0, 157) + '…' : note.text;

  // Countdown to expiry
  const expiresIn = (() => {
    const ms = note.expiresAt - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();

  return (
    <div style={{
      background: 'linear-gradient(90deg, rgba(56,139,253,0.10) 0%, rgba(56,139,253,0.04) 100%)',
      borderBottom: '1px solid rgba(56,139,253,0.3)',
      padding: '5px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexShrink: 0,
      minHeight: 30,
    }}>
      <span style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 9, fontWeight: 700, letterSpacing: '.12em',
        color: 'var(--accent)', whiteSpace: 'nowrap',
        textTransform: 'uppercase', flexShrink: 0,
      }}>◈ SESSION PLAN</span>

      <span style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11, color: 'var(--text)', flex: 1,
        lineHeight: 1.5, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{preview}</span>

      <span style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 9, color: 'var(--muted)',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {fmtDateShort(note.createdAt)} · {fmtTime(note.createdAt)}
        {expiresIn ? ` · ⏱ ${expiresIn}` : ''}
      </span>

      <button onClick={onEdit} style={{
        background: 'transparent', border: '1px solid var(--border)',
        color: 'var(--muted)', borderRadius: 4, padding: '2px 8px',
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
        cursor: 'pointer', letterSpacing: '.06em', flexShrink: 0,
      }}>EDIT</button>

      <button onClick={onDismiss} style={{
        background: 'transparent', border: 'none',
        color: 'var(--muted)', fontSize: 14, lineHeight: 1,
        cursor: 'pointer', padding: '0 2px', flexShrink: 0,
      }}>×</button>
    </div>
  );
}

// ── SessionNotesPanel ─────────────────────────────────────────────────────────
export default function SessionNotesPanel({ onNoteChange, placeholder }) {
  const [note,    setNote]    = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');
  const [sync,    setSync]    = useState('loading'); // 'loading'|'ok'|'saving'|'err'
  const [saved,   setSaved]   = useState(false);
  const [log,     setLog]     = useState(null); // loaded lazily

  // ── Load active note from worker on mount ─────────────────────────────────
  useEffect(() => {
    sessionNoteGet()
      .then(data => {
        if (data && !isExpired(data)) {
          setNote(data);
          onNoteChange?.(data);
        } else if (data && isExpired(data)) {
          // Silently archive expired note then clear it
          archiveNote(data).catch(() => {});
          sessionNoteDelete().catch(() => {});
          onNoteChange?.(null);
        }
        setSync('ok');
      })
      .catch(() => { setSync('err'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Archive a note to the server log
  const archiveNote = useCallback(async (noteToArchive) => {
    try {
      const { notes: existing } = await sessionLogGet();
      if (existing.find(n => n.id === noteToArchive.id)) return; // already archived
      const updated = [noteToArchive, ...existing].slice(0, 90);
      await sessionLogPut(updated);
    } catch { /* non-critical */ }
  }, []);

  // ── Save/delete helpers ───────────────────────────────────────────────────
  const persist = useCallback(async (newNote) => {
    setSync('saving');
    try {
      await sessionNotePut(newNote);
      setSync('ok');
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch { setSync('err'); }
  }, []);

  const saveNote_ = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    const now = new Date().toISOString();
    const newNote = {
      id:        note?.id || `sn-${Date.now()}`,
      text,
      createdAt: note?.createdAt || now,
      updatedAt: now,
      expiresAt: nextMarketClose(),
    };
    setNote(newNote);
    setEditing(false);
    setDraft('');
    onNoteChange?.(newNote);
    persist(newNote);
    // Always archive latest version
    archiveNote(newNote).catch(() => {});
  }, [draft, note, onNoteChange, persist, archiveNote]);

  const deleteNote_ = () => {
    if (!confirm('Delete session note? It stays in the journal log.')) return;
    if (note) archiveNote({ ...note, deletedAt: new Date().toISOString() }).catch(() => {});
    sessionNoteDelete().catch(() => {});
    setNote(null);
    setEditing(false);
    onNoteChange?.(null);
  };

  const startNew  = () => { setDraft(''); setEditing(true); setSaved(false); };
  const startEdit = () => { setDraft(note?.text || ''); setEditing(true); setSaved(false); };
  const cancel    = () => { setEditing(false); setDraft(''); };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); saveNote_(); }
    if (e.key === 'Escape') cancel();
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const mono = { fontFamily: "'IBM Plex Mono', monospace" };
  const label = { ...mono, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--muted)' };
  const btn = (v = 'default') => ({
    ...mono, fontSize: 10, fontWeight: 600, border: 'none', borderRadius: 4,
    padding: '4px 11px', cursor: 'pointer', letterSpacing: '.04em',
    ...(v === 'primary' ? { background: 'var(--accent)', color: '#fff' } : {}),
    ...(v === 'default' ? { background: 'var(--bg-hover)', color: 'var(--muted)', border: '1px solid var(--border)' } : {}),
    ...(v === 'danger'  ? { background: 'transparent', color: 'var(--down)', border: '1px solid var(--down-soft)' } : {}),
  });

  const active = note && !isExpired(note);
  const syncColor = { loading: 'var(--muted)', ok: 'var(--up)', saving: 'var(--warn)', err: 'var(--down)' }[sync];

  const defaultPlaceholder = 'Today\'s strategy, bias, key levels…\n\ne.g. Neutral-to-bearish. Fade SPY bounces into 520 R. Stop above 522. Watch VIX > 18 for hedges. No chasing.';

  // ── Editing form ──────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div style={{ padding: '10px 12px', height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={label}>{active ? '✎ EDIT SESSION PLAN' : '+ NEW SESSION PLAN'}</div>
        <textarea
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || defaultPlaceholder}
          style={{
            flex: 1, background: 'var(--bg)', border: '1px solid var(--border-strong)',
            borderRadius: 6, color: 'var(--text)', ...mono, fontSize: 11,
            lineHeight: 1.65, padding: '10px 12px', resize: 'none', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e  => e.target.style.borderColor = 'var(--border-strong)'}
        />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={saveNote_} disabled={!draft.trim()} style={btn('primary')}>✓ Save</button>
          <button onClick={cancel} style={btn('default')}>Cancel</button>
          <span style={{ ...mono, fontSize: 9, color: 'var(--faint)', marginLeft: 'auto' }}>⌘↵ save · Esc cancel</span>
        </div>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (sync === 'loading') {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ ...mono, fontSize: 11, color: 'var(--muted)' }}>⟳ loading…</span>
      </div>
    );
  }

  // ── Empty ─────────────────────────────────────────────────────────────────
  if (!active) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: 16 }}>
        <div style={{ fontSize: 22, opacity: 0.35 }}>📋</div>
        <div style={{ ...mono, fontSize: 10, color: 'var(--muted)', lineHeight: 1.7 }}>
          No session plan yet.<br />
          <span style={{ color: 'var(--faint)', fontSize: 9 }}>Expires at next market close (4 PM ET, Mon–Fri)</span>
        </div>
        <button onClick={startNew} style={{ ...btn('primary'), padding: '6px 18px', fontSize: 11 }}>
          + New Session Plan
        </button>
      </div>
    );
  }

  // ── Expiry countdown ──────────────────────────────────────────────────────
  const expiresIn = (() => {
    const ms = note.expiresAt - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();

  // ── Active note view ──────────────────────────────────────────────────────
  return (
    <div style={{ padding: '10px 12px', height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={label}>◈ TODAY'S PLAN</span>
        <span style={{
          ...mono, fontSize: 8, background: 'rgba(56,139,253,0.12)',
          color: 'var(--accent)', border: '1px solid rgba(56,139,253,0.25)',
          borderRadius: 3, padding: '1px 5px',
          textTransform: 'uppercase', letterSpacing: '.06em',
        }}>ACTIVE</span>
        {sync === 'saving' && <span style={{ ...mono, fontSize: 9, color: syncColor }}>⟳</span>}
        {saved && <span style={{ ...mono, fontSize: 9, color: 'var(--up)' }}>✓ Saved</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button onClick={startEdit} style={{ ...btn('default'), padding: '3px 9px', fontSize: 9 }}>✎ Edit</button>
          <button onClick={deleteNote_} style={{ ...btn('danger'), padding: '3px 9px', fontSize: 9 }}>✕</button>
        </div>
      </div>

      <div style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px', overflowY: 'auto' }}>
        <pre style={{ margin: 0, ...mono, fontSize: 11, lineHeight: 1.7, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {note.text}
        </pre>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
        <span style={{ ...mono, fontSize: 9, color: 'var(--faint)' }}>
          Set {fmtTime(note.createdAt)}
          {note.updatedAt !== note.createdAt ? ` · edited ${fmtTime(note.updatedAt)}` : ''}
        </span>
        {expiresIn && (
          <span style={{ ...mono, fontSize: 9, color: 'var(--muted)', marginLeft: 'auto' }}>
            ⏱ expires in {expiresIn}
          </span>
        )}
      </div>
    </div>
  );
}
